import * as React from "react";
import { FunctionComponent } from "react";
import {
  HighlightsModuleSettings,
  ColorScheme,
  StoreState,
  MultiBoxItemData,
  HighlightsLayoutVariant,
  MultiBoxDisplayType,
  HighlightModuleSettings,
  Modules,
  Pictures,
  AspectRatio,
  BaseModuleProps,
  Language,
  TranslatedModule,
  ImageModuleSettings,
  PageState,
  RichEditorState,
} from "../../types";
import MultiBox from "../MultiBox";
import { connect, MapStateToProps } from "react-redux";
import {
  getActiveColorScheme,
  getModulesByParentId,
  resolveButtonLink,
  getTranslatedButtonModules,
  getTranslatedModulesByParentId,
} from "../../utils/utils";
import { defaultSettings } from "../../reducers/modules/byId";
import * as deepExtend from "deep-extend";
import { getActiveSite } from "../../selectors/sites";
import ButtonModule from "./ButtonModule";
import { getPictureById } from "../../selectors/pictures";
import { APISite } from "../../../server/types";
import ModuleWithHeadings from "../ModuleWithHeadings";
import RichEditorWrapper from "../RichEditorWrapper";

interface Props extends BaseModuleProps<HighlightsModuleSettings> {
  layoutVariant?: HighlightsLayoutVariant;
}

interface StateProps {
  scheme: ColorScheme;
  layoutVariant: HighlightsLayoutVariant;
  displayType: MultiBoxDisplayType;
  itemsData: MultiBoxItemData[];
  imagesAspectRatio: AspectRatio;
  hasHighlights: boolean;
  richEditor: RichEditorState;
}

const HighlightsModule: FunctionComponent<Props & StateProps> = ({
  translatedModule: {
    settings: { textAlign, maxColumnsCount, collapsedLinesCount },
    translation: {
      settings: { title, subtitle },
      languageId,
    },
    id,
  },
  isLayoutPreview,
  queries,
  scheme,
  displayType,
  layoutVariant,
  itemsData,
  isPreview,
  imagesAspectRatio,
  isFirstOnPage,
  hasHighlights,
  richEditor,
}) => (
  <ModuleWithHeadings
    id={id}
    className="HighlightsModule"
    title={title}
    subtitle={subtitle}
    style={{ color: scheme.main.text }}
  >
    <MultiBox
      {...{
        title,
        subtitle,
        textAlign,
        imagesAspectRatio,
        isPreview,
        isLayoutPreview,
        queries,
        scheme,
        layoutVariant,
        languageId,
        displayType,
        itemsData,
        isFirstOnPage,
        maxColumnsCount,
        placeholdersShown: !hasHighlights,
        collapsedLinesCount: collapsedLinesCount,
        richEditor,
      }}
    />
  </ModuleWithHeadings>
);

const getItemsData = ({
  pictures,
  modules,
  module,
  pageId,
  languageId,
  isPreview,
  scheme,
  pages,
  site,
}: {
  pictures: Pictures;
  modules: Modules;
  module: TranslatedModule<HighlightsModuleSettings>;
  pageId: string;
  languageId: Language;
  isPreview: boolean;
  scheme: ColorScheme;
  pages: PageState;
  site: APISite;
}): { itemsData: MultiBoxItemData[]; hasHighlights: boolean } => {
  const { siteId } = module;
  const highlights = getTranslatedModulesByParentId<HighlightModuleSettings>({
    modules,
    parentId: module.id,
    pageId,
    moduleType: "HighlightModule",
    languageId,
  });

  const hasHighlights = !!highlights.length;

  const finalHighlights = hasHighlights
    ? highlights
    : // Construct Array with sequential numbers as strings
      Object.keys(Array.from(new Array(2))).map<
        TranslatedModule<HighlightModuleSettings>
      >((id) => {
        const highlightModule: TranslatedModule<HighlightModuleSettings> = {
          id,
          colorSchemeId: null,
          siteId,
          type: "HighlightModule",
          pageId,
          parentId: module.id,
          settings: deepExtend({}, defaultSettings.HighlightModule.global),
          translation: {
            languageId,
            exists: true,
            settings: deepExtend({}, defaultSettings.HighlightModule.language),
          },
          languages: [languageId],
        };
        return highlightModule;
      });

  // TODO: check if pictures are loaded
  // Use getPictureById to retrieve pictures and crop informations!
  const itemsData: MultiBoxItemData[] = finalHighlights.map((highlight) => {
    const {
      id: moduleId,
      settings: { textAlign },
      translation: {
        settings: { title, subtitle },
      },
    } = highlight;

    const images = getModulesByParentId<ImageModuleSettings>(
      modules,
      moduleId,
      pageId,
      "ImageModule"
    );

    const imageSettings = images.length ? images[0].settings : undefined;

    const picture = getPictureById(
      pictures,
      imageSettings && imageSettings.pictureId
    );

    const translatedButtons = getTranslatedButtonModules({
      languageId,
      modules,
      moduleId,
      pageId,
    });

    const buttons = translatedButtons.map((translatedButton) => (
      <ButtonModule
        key={translatedButton.id}
        translatedModule={translatedButton}
        scheme={scheme}
        isPreview={isPreview}
      />
    ));

    const firstButton = translatedButtons[0];
    const link =
      firstButton &&
      resolveButtonLink({
        isPreview,
        pages,
        site,
        translatedModule: firstButton,
      });

    const itemData: MultiBoxItemData = {
      id: moduleId,
      title,
      subtitle,
      textAlign,
      picture,
      link,
      text: (
        <RichEditorWrapper
          module={highlight}
          pageId={pageId}
          languageId={languageId}
          isPreview={isPreview}
          scheme={scheme}
          className={`TextAlign--${textAlign.description}`}
        />
      ),
      buttons,
      imageSettings,
    };

    return itemData;
  });

  return { itemsData, hasHighlights };
};

const mapStateToProps: MapStateToProps<StateProps, Props, StoreState> = (
  { colorSchemes, mediaLibrary, modules, sites, pages, richEditor },
  { translatedModule, layoutVariant, isPreview, pageId }
): StateProps => {
  const site = getActiveSite(sites);
  const { languageId } = translatedModule.translation;
  const {
    displayType,
    imagesAspectRatio,
    layoutVariant: moduleLayoutVariant,
  } = translatedModule.settings;
  const { pictures } = mediaLibrary;
  const scheme = getActiveColorScheme(colorSchemes, site, translatedModule);

  const { itemsData, hasHighlights } = getItemsData({
    pictures,
    site,
    pages,
    modules,
    module: translatedModule,
    pageId,
    languageId,
    isPreview,
    scheme,
  });

  return {
    scheme,
    displayType,
    layoutVariant: layoutVariant || moduleLayoutVariant,
    imagesAspectRatio,
    itemsData,
    hasHighlights,
    richEditor,
  };
};

export default connect(mapStateToProps)(HighlightsModule);
